import {
  IPatient,
  IPatientAddress,
  IPatientIdentifier,
  IPatientName,
  IPatientPhoto, IPatientTelecom
} from '../interfaces/patient.interface';
import { Coding } from '../../shared/interfaces/coding.interface';
import { PatientIdentifier } from './patient-identifier.class';
import { has, get, cloneDeep } from 'lodash';

export class Patient implements IPatient {
  active: boolean;
  address: IPatientAddress[];
  associated_file: any[];
  authorization: { reference: string; resourceType: string }[];
  birthDate: string;
  careProvider: { display: string; reference: string }[];
  communication: { language: { coding: Coding[] }; preferred: boolean }[];
  contact: any[];
  deceasedBoolean: boolean;
  deceasedDateTime: string;
  demographics: { race: Coding; ethnicity: Coding };
  gender: string;
  id: string;
  identifier: IPatientIdentifier[];
  link: any[];
  managingOrganization: string;
  martialStatus: { coding: Coding[] };
  multipleBirthBoolean: boolean;
  multipleBirthInteger: string;
  name: IPatientName[];
  order: any[];
  photo: IPatientPhoto[];
  record: any[];
  telecom: IPatientTelecom[];
  resourceType: string = 'Patient';

  static Factory(orgId: string, orgName: string) {
    const patient = this.Clone( {
      identifier: [
        {
          use: 'official',
          type: {
            coding: [
              {
                system: 'http://hl7.org/fhir/v2/0203',
                code: 'LR',
                display: 'Local Registry ID'
              }
            ]
          },
          system: 'urn:oid:1.2.36.146.595.217.0.1',
          value: '',
          period: {
            start: '',
            end: ''
          },
          assigner: {
            reference: orgId,
            display: orgName
          }
        },
        {
          use: 'official',
          type: {
            coding: [
              {
                system: 'http://hl7.org/fhir/v2/0203',
                code: 'ACSN',
                display: 'Accession Identifier'
              }
            ]
          },
          system: 'urn:oid:1.2.36.146.595.217.0.1',
          value: '',
          period: {
            start: '',
            end: ''
          },
          assigner: {
            display: 'Hospital',
            reference: ''
          }
        },
        {
          use: 'official',
          type: {
            coding: [
              {
                system: 'http://hl7.org/fhir/v2/0203',
                code: 'DL',
                display: 'Driver license number'
              }
            ]
          },
          system: 'urn:oid:1.2.36.146.595.217.0.1',
          value: '',
          period: {
            start: '',
            end: ''
          },
          assigner: {
            display: 'Ministry Of Transportation',
            reference: ''
          }
        },
        {
          use: 'official',
          type: {
            coding: [
              {
                system: 'http://hl7.org/fhir/v2/0203',
                code: 'PPN',
                display: 'Passport number'
              }
            ]
          },
          system: 'urn:oid:1.2.36.146.595.217.0.1',
          value: '',
          period: {
            start: '',
            end: ''
          },
          assigner: {
            display: 'Ministry Of Interior',
            reference: ''
          }
        }
      ],
      active: true,
      name: [
        {
          use: 'official',
          text: '',
          family: [],
          given: [],
          prefix: [],
          suffix: [],
          period: {
            start: '',
            end: ''
          }
        }
      ],
      telecom: [
        {
          system: 'phone',
          use: 'mobile',
          value: '',
          rank: 1,
          period: {
            start: '',
            end: ''
          }
        },
        {
          system: 'phone',
          use: 'home',
          value: '',
          rank: 1,
          period: {
            start: '',
            end: ''
          }
        },
        {
          system: 'email',
          use: '',
          value: '',
          rank: 1,
          period: {
            start: '',
            end: ''
          }
        }
      ],
      gender: 'Male',
      birthDate: '',
      deceasedBoolean: false,
      deceasedDateTime: '',
      address: [
        {
          use: 'home',
          type: 'physical',
          text: '',
          line: [],
          city: '',
          district: '',
          state: '',
          postalCode: '',
          country: '',
          period: {
            start: '',
            end: ''
          }
        }
      ],
      martialStatus: {
        coding: [
          {
            system: 'http://hl7.org/fhir/v3/MaritalStatus',
            code: 'UNK',
            display: 'Unknown'
          }
        ]
      },
      multipleBirthBoolean: false,
      multipleBirthInteger: '',
      photo: [
        {
          contentType: '',
          language: '',
          data: '',
          // url: 'http://cdn.techjuice.pk/wp-content/uploads/2015/02/wallpaper-for-facebook-profile-photo.jpg',
          url: '',
          size: '',
          hash: '',
          title: '',
          creation: ''
        }
      ],
      contact: [],
      communication: [
        {
          language: {
            coding: [
              {
                system: 'urn:ietf:bcp:47',
                code: 'EN',
                display: 'English'
              }
            ]
          },
          preferred: true
        }
      ],
      careProvider: [
        {
          reference: '',
          display: ''
        }
      ],
      managingOrganization: orgId,
      link: [],
      record: [],
      associated_file: [],
      order: [],
      demographics: {
        race: {
          code: '',
          display: ''
        },
        ethnicity: {
            code: '',
            display: ''
        }
      }
    });
    patient.setMRN('', orgId, orgName);
    return patient;
  }

  set MRN(mrn) {
    this. setMRN(mrn);
  }

  get MRN() {
    return get(this.getMRN(), 'value');
  }

  static Clone(patient) {
    const clonedPatient = Object.assign(new this(), cloneDeep(patient)) as Patient;

    if (has(clonedPatient, 'demographics.ethnicity.race')) {
      delete clonedPatient.demographics.ethnicity['race'];
    }

    return clonedPatient ;
  }


  setMRN(mrn, orgId?, orgName?) {
    let identifier = this.getMRN();

    if (!identifier) {
      identifier = PatientIdentifier.CreateMRN(mrn, orgId, orgName);

      if (!this.identifier) {
        this.identifier = [];
      }

      this.identifier.unshift(identifier);
    } else {
      identifier.value = mrn;
    }
  }

  getMRN() {
    return this.identifier && this.identifier
      .find(identifier => get(identifier, 'type.coding[0].code', '').toUpperCase() === 'MRN');
  }
}
